import React, { useState, useEffect, useRef } from 'react';
import { Box, Grid, Typography, Divider, IconButton, CircularProgress, Tooltip, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import axiosInstance from '../../../lib/axios';
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';

const PrintBarcode = ({ handleClose, MstAssetId, MstAssetDtlId }) => {
    const [barcodeItems, setBarcodeItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const contentRef = useRef(null);
    const handlePrint = useReactToPrint({ contentRef });

    useEffect(() => {
        const fetchBarcodeData = async () => {
            if (!MstAssetId) {
                setError("No asset ID provided");
                setLoading(false);
                toast.error("No asset ID provided for barcode printing");
                return;
            }

            try {
                setLoading(true);
                const response = await axiosInstance.post("MstAsset/RetriveMstAssetDtl",
                    {
                        MstAssetId: MstAssetId,
                        MstAssetDtlId: MstAssetDtlId || 1,
                        Flag: "R",
                        ActionBy: parseInt(sessionStorage.getItem('UserId')) || 2
                    }
                );

                if (response.data.Status === 0 && Array.isArray(response.data.Data)) {
                    const items = response.data.Data.map((item, index) => ({
                        id: item.BarcodeNo || `AS${index.toString().padStart(5, '0')}`,
                        details: {
                            ItmName: item.ItmName || "N/A",
                            ModelOemPartNo: item.ModelOemPartNo || "N/A",
                            ItmCode: item.ItmCode || "N/A",
                            SgrnNo: item.SgrnNo || "N/A",
                            VendName: item.VendName || "N/A",
                            ModelBrand: item.ModelBrand || "N/A",
                            ModelNo: item.ModelNo || "N/A",
                            WarrantyPeriod: item.WarrantyPeriod || "0",
                            WarrantyUpto: item.WarrantyUpto || "N/A",
                            ModelColour: item.ModelColour || "N/A",
                            ModelClass: item.ModelClass || "N/A",
                            SerProvName: item.SerProvName || "N/A",
                        }
                    }));
                    setBarcodeItems(items);
                } else {
                    toast.error(response.data.Message || "Invalid API response");
                }
            } catch (error) {
                toast.error(`Failed to load barcode data: ${error.message || 'Unknown error'}`);
            } finally {
                setLoading(false);
            }
        };

        fetchBarcodeData();
    }, [MstAssetId, MstAssetDtlId]);

    return (
        <Box sx={{ px: 1, height: '85vh', overflowY: 'auto', maxWidth: '100vw', margin: 'auto' }}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: '#fff',
                    paddingBottom: 1,
                }}
            >
                <Typography variant="h5">Asset Barcode</Typography>
                <Box>
                    <IconButton onClick={handlePrint} sx={{ mr: 1 }}>
                        <Tooltip title="Print">
                            <PrintIcon color="primary" />
                        </Tooltip>
                    </IconButton>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>
            <Divider sx={{ mb: 1 }} />

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100%">
                    <CircularProgress size="3rem" />
                </Box>
            ) : error ? (
                <Typography color="error" align="center">
                    {error}
                </Typography>
            ) : barcodeItems.length === 0 ? (
                <Typography align="center">
                    No barcode data available for this asset
                </Typography>
            ) : (
                <Box sx={{ px: 1, m:2 }} ref={contentRef}>
                    <Grid container spacing={2} >
                        {barcodeItems.map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 2,
                                        border: '1px dashed #ccc',
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                                        <Box
                                            sx={{
                                                border: '1px solid #ddd',
                                                p: 1,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                backgroundColor: '#f9f9f9'
                                            }}
                                        >
                                            <Barcode
                                                value={item.id}
                                                format="CODE128"
                                                width={2}
                                                height={30}
                                                displayValue={true}
                                                fontSize={14}
                                                margin={0}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                border: '1px solid #ddd',
                                                p: 1,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                backgroundColor: '#f9f9f9'
                                            }}
                                        >
                                            <QRCodeSVG
                                                value={item.id}
                                                size={64}
                                                level="H"
                                                marginSize={4}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ mt: 1, fontSize: '0.75rem' }}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}><Typography><strong>Item Name:</strong> {item.details.ItmName}</Typography></Grid>
                                            <Grid item xs={6}><Typography><strong>Part No:</strong> {item.details.ModelOemPartNo}</Typography></Grid>
                                            <Grid item xs={6}> <Typography><strong>Item Code:</strong> {item.details.ItmCode}</Typography></Grid>
                                            <Grid item xs={6}><Typography><strong>Model No:</strong> {item.details.ModelNo}</Typography></Grid>
                                            <Grid item xs={6}><Typography><strong>Class:</strong> {item.details.ModelClass}</Typography></Grid>
                                            <Grid item xs={6}><Typography><strong>SGRN No:</strong> {item.details.SgrnNo}</Typography> </Grid>
                                            <Grid item xs={6}><Typography><strong>Warranty:</strong> {item.details.WarrantyPeriod} Year</Typography></Grid>
                                            <Grid item xs={6}><Typography><strong>Warranty Upto:</strong> {item.details.WarrantyUpto}</Typography></Grid>
                                            <Grid item xs={6}><Typography><strong>Brand:</strong> {item.details.ModelBrand}</Typography></Grid>
                                            <Grid item xs={6}><Typography><strong>Color:</strong> {item.details.ModelColour}</Typography> </Grid>
                                            <Grid item xs={6}><Typography><strong>Vendor:</strong> {item.details.VendName}</Typography></Grid>
                                            <Grid item xs={6}><Typography><strong>Service Provider:</strong> {item.details.SerProvName}</Typography></Grid>
                                        </Grid>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};
export default PrintBarcode;